import React from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import 'chartjs-adapter-date-fns';
import { baseOptions, timeAxis, valueAxis } from '../../utils/chartConfig';
import { roomTemperature } from '../../utils/readings';

// Sensing view: what the device measured over the selected London day. Room
// temperature and heating on/off up front; the probe temperatures and humidity
// are in the legend, hidden until clicked.
const MeasuredChart = ({ measurements, heatingStatus, loading, selectedDate }) => {
    if (loading) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin mb-3" />
                <p className="text-sm">Loading readings...</p>
            </div>
        );
    }
    const rows = measurements || [];
    const status = heatingStatus || [];
    if (rows.length === 0 && status.length === 0) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <p className="text-sm">No readings for this day</p>
            </div>
        );
    }

    const hasHumidity = rows.some(r => r.room_humidity != null);
    const series = (label, pick, extra) => ({
        label,
        data: rows.map(r => ({ x: r.time, y: pick(r) })),
        fill: false,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: 'y',
        ...extra,
    });

    const datasets = [
        series('Room temperature', roomTemperature, { borderColor: 'rgba(244, 63, 94, 1)', order: 1 }),  // rose-500
        {
            label: 'Heating on',
            data: status.map(s => ({ x: s.time, y: s.heating_status })),
            fill: true,
            stepped: true,
            pointRadius: 0,
            borderWidth: 0,
            backgroundColor: 'rgba(251, 191, 36, 0.35)',  // amber-400
            yAxisID: 'y1',
            order: 5,
        },
        series('Flow probe', r => r.flow_temperature, { borderColor: 'rgba(249, 115, 22, 0.8)', borderWidth: 1.25, hidden: true, order: 2 }),
        series('Return probe', r => r.return_temperature, { borderColor: 'rgba(14, 165, 233, 0.8)', borderWidth: 1.25, hidden: true, order: 3 }),
    ];
    if (hasHumidity) {
        datasets.push(series('Humidity', r => r.room_humidity, {
            borderColor: 'rgba(16, 185, 129, 0.8)', borderWidth: 1.25, hidden: true, yAxisID: 'y2', order: 4,
        }));
    }

    const options = baseOptions({
        label: (context) => {
            const name = context.dataset.label || '';
            const y = context.parsed.y;
            if (y == null) return name;
            if (context.dataset.yAxisID === 'y1') return `${name}: ${y > 0.5 ? 'yes' : 'no'}`;
            if (context.dataset.yAxisID === 'y2') return `${name}: ${y.toFixed(0)}%`;
            return `${name}: ${y.toFixed(1)}°C`;
        },
        scales: {
            x: timeAxis(selectedDate),
            y: valueAxis('°C'),
            y1: { type: 'linear', display: false, suggestedMin: 0, suggestedMax: 1, grid: { drawOnChartArea: false } },
            y2: { type: 'linear', display: false, min: 0, max: 100, grid: { drawOnChartArea: false } },
        },
    });

    return (
        <div style={{ height: '100%' }}>
            <Line data={{ datasets }} options={options} />
        </div>
    );
};

export default MeasuredChart;
